import { useEffect, useState } from "react";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { ChevronDown, MapPin, Star } from "lucide-react";

const HERO_SLIDES = [
  "https://customer-assets.emergentagent.com/job_retreat-booking-5/artifacts/cs8siotk_image.png",
  "https://customer-assets.emergentagent.com/job_retreat-booking-5/artifacts/2i5fwt5h_image.png",
];

export const Hero = ({ lang }) => {
  const t = useT(lang);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setActive((i) => (i + 1) % HERO_SLIDES.length);
    }, 6000);
    return () => clearInterval(id);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden"
    >
      <div className="absolute inset-0">
        {HERO_SLIDES.map((src, i) => (
          <img
            key={src}
            src={src}
            alt={`Skyline Retreat ${i + 1}`}
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[2000ms] ease-in-out"
            style={{ opacity: i === active ? 1 : 0 }}
            loading={i === 0 ? "eager" : "lazy"}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/30 to-[#4a3520]/85" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-20 min-h-[100svh] flex flex-col justify-end">
        <div className="max-w-3xl">
          <div className="reveal flex items-center gap-2 text-[#f7e8c9] text-xs tracking-[0.25em] uppercase mb-5">
            <MapPin className="w-3.5 h-3.5" />
            {t.hero.eyebrow}
          </div>

          <h1
            data-testid="hero-title"
            className="reveal reveal-delay-1 font-serif-display text-5xl sm:text-6xl lg:text-7xl text-[#f7f1e6] leading-[1.02] mb-6"
          >
            {t.hero.title}
          </h1>

          <p className="reveal reveal-delay-2 text-base sm:text-lg text-[#f0e3c7] max-w-2xl leading-relaxed mb-8">
            {t.hero.subtitle}
          </p>

          <div className="reveal reveal-delay-3 flex flex-wrap items-center gap-4">
            <Button
              data-testid="hero-cta-btn"
              size="lg"
              onClick={() => scrollTo("cabins")}
              className="bg-[#f7f1e6] hover:bg-[#efe5d3] text-[#4a3520] rounded-full px-8 h-12 text-sm font-medium tracking-wide"
            >
              {t.hero.cta}
            </Button>

            <div className="flex items-center gap-2 text-[#f0e3c7] text-sm">
              <div className="flex items-center gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className="w-4 h-4 fill-[#e8c875] text-[#e8c875]"
                  />
                ))}
              </div>
              <span>5.0 · 48 Google reviews</span>
            </div>
          </div>

          {/* Slideshow indicators */}
          <div
            data-testid="hero-slide-dots"
            className="reveal reveal-delay-3 mt-8 flex gap-2"
          >
            {HERO_SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                aria-label={`Slide ${i + 1}`}
                className={`h-1 rounded-full transition-all duration-500 ${
                  i === active
                    ? "w-10 bg-[#f7f1e6]"
                    : "w-5 bg-[#f7f1e6]/40 hover:bg-[#f7f1e6]/70"
                }`}
              />
            ))}
          </div>
        </div>

        <button
          data-testid="hero-scroll-down"
          onClick={() => scrollTo("cabins")}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#f0e3c7] text-xs tracking-widest uppercase flex flex-col items-center gap-2 opacity-80 hover:opacity-100 transition"
        >
          {t.hero.scroll}
          <ChevronDown className="w-4 h-4 animate-bounce" />
        </button>
      </div>
    </section>
  );
};
